// The class dismissal board, grouped the way a teacher reads it at 3 pm (plan §7 teacher app).
import { time } from "../shared/format.ts";

export interface BoardChild {
  id: string; first: string; last: string; room_id: string | null; photo_sha256: string | null;
  /** The child's grade (their own, else their room's); null when the school has not set one. */
  grade?: string | null;
  /** How they usually go home, in the office's words. */
  dismissal_plan?: string | null;
  /** Leaving without an adult, by the school's rules and the parent's consents. */
  leave?: { home_alone: boolean; siblings: { id: string; first: string; last: string }[] } | null;
  state: "expected" | "in" | "out" | "absent" | string;
  custodian: string | null;
  last_event: {
    seq: number; type: string; adult_id: string | null; adult?: { first: string; last: string } | null;
    claimed_name: string | null; server_ts: number; server_flags: string[]; to?: string | null;
    /** A correction that withdrew an absence marked by mistake. */
    restore?: boolean;
    /** A drop-off with no adult: the child came in on their own. */
    walk_in?: boolean;
    /** A pickup with no adult: went home on their own, or with this sibling (their first name). */
    home_alone?: boolean;
    sibling?: string | null;
    reunification?: boolean;
  } | null;
}

export interface Board { children: BoardChild[]; flagged: unknown[]; head: { seq: number; hash: string }; rules?: { walk_in_from: string | null }; reunification?: { since: number; place: string | null } | null }

export type GroupId = "door" | "in" | "after_school" | "expected" | "out" | "absent";

export const GROUPS: { id: GroupId; title: string }[] = [
  { id: "door", title: "Called to the door" },
  { id: "in", title: "In class" },
  { id: "after_school", title: "At after-school" },
  { id: "expected", title: "Not here yet" },
  { id: "out", title: "Gone home" },
  { id: "absent", title: "Absent" },
];

export function groupOf(c: BoardChild): GroupId {
  if (c.state === "out") return "out";
  if (c.state === "absent") return "absent";
  if (c.state === "in" && c.custodian === "door") return "door";
  if (c.state === "in" && c.custodian === "after_school") return "after_school";
  if (c.state === "in") return "in";
  return "expected";
}

export function groupBoard(children: BoardChild[]): { id: GroupId; title: string; children: BoardChild[] }[] {
  return GROUPS.map((g) => ({ ...g, children: children.filter((c) => groupOf(c) === g.id) })).filter((g) => g.children.length);
}

/** "Left with Joe Lopez at 3:02 PM", "Dropped off at 8:10 AM", "Not here yet"; without the time where it shows beside it. */
export function statusLine(c: BoardChild, tz: string, withTime = true): string {
  const e = c.last_event;
  if (!e) return c.state === "expected" ? "Not here yet" : "";
  const line = statusText(e, tz);
  return withTime ? line : line.replace(/ at \d{1,2}:\d{2}\s?[AP]M$/, "");
}

function statusText(e: NonNullable<BoardChild["last_event"]>, tz: string): string {
  const at = time(e.server_ts, tz);
  const who = e.adult ? `${e.adult.first} ${e.adult.last}` : e.claimed_name;
  switch (e.type) {
    case "pickup": return e.reunification ? (who ? `Released to ${who} at the reunification point, ${at}` : `Released at the reunification point, ${at}`) : e.home_alone ? `Went home on their own at ${at}` : e.sibling ? `Left with ${e.sibling} (sibling) at ${at}` : who ? `Left with ${who} at ${at}` : `Left at ${at}`;
    case "dropoff": return e.server_flags?.includes("late_arrival") ? (e.walk_in ? `Came in on their own, late, at ${at}` : `Arrived late at ${at}`) : e.walk_in ? `Came in on their own at ${at}` : who ? `Dropped off by ${who} at ${at}` : `Dropped off at ${at}`;
    case "absence": return `Marked absent at ${at}`;
    case "correction": return e.restore ? `Absence withdrawn at ${at}` : `Corrected at ${at}`;
    case "transfer": return e.to === "door" ? `Sent to the door at ${at}` : e.to === "after_school" ? `Sent to after-school at ${at}` : `Back in class at ${at}`;
    default: return `Updated at ${at}`;
  }
}

export const counts = (children: BoardChild[]) => {
  const n = { here: 0, gone: 0, absent: 0, expected: 0 };
  for (const c of children) {
    const g = groupOf(c);
    if (g === "out") n.gone++; else if (g === "absent") n.absent++; else if (g === "expected") n.expected++; else n.here++;
  }
  return n;
};

/** Local calendar date (YYYY-MM-DD) in the site's time zone, for today's requests. */
export const siteDate = (tz: string, now = Date.now()) => new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(now));

/** Today's afternoon bus for each child who rides one (the transport module's going-home list). */
/** `bus`: the bus to look for at dismissal; `replaces`: the usual one when another stands in today. */
export type BusToday = Record<string, { route: string; bus?: string | null; replaces?: string | null; not_riding: { late: boolean; why: string } | null }>;

/**
 * How a child goes home today, for the teacher at dismissal: their bus (unless not riding today),
 * on their own or with a sibling (the parent's consent), the office's note, or collected by an adult.
 */
export function goingHome(c: BoardChild, bus: BusToday | null): { key: string; label: string } {
  const b = bus?.[c.id];
  if (b && !b.not_riding) return { key: `bus:${b.route}`, label: `Bus · ${b.route}${b.bus ? ` · ${b.bus}${b.replaces ? ` (for ${b.replaces} today)` : ""}` : ""}` };
  if (c.leave?.home_alone) return { key: "alone", label: "Goes home on their own" };
  if (c.leave?.siblings.length) return { key: "sibling", label: `With ${c.leave.siblings.map((s) => s.first).join(" or ")}` };
  const note = b?.not_riding ? (b.not_riding.why === "absent" ? "" : "Not on the bus today · ") : "";
  if (c.dismissal_plan) return { key: `plan:${c.dismissal_plan}`, label: `${note}${c.dismissal_plan}` };
  return { key: "adult", label: `${note}Collected by an adult` };
}
